import React, {
  FC,
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useGetProjectsQuery } from "../../../api/projectsApi";
import { usePagination } from "../../../utils/hooks/usePagination";
import Pagination from "../../../components/Pagination/Pagination";
import ProjectItemList from "../components/ProjectItemList/ProjectItemList";
import { H1 } from "../../../components/Text/Text";
import Search from "../../../components/Search/Search";
import OrderBy from "../../../components/OrderBy/OrderBy";
import StatusTag from "../../../components/StatusTag/StatusTag";
import { useNavigate, useSearchParams } from "react-router-dom";
import StatusFiltersSidebar from "../../../components/StatusFiltersSidebar/StatusFiltersSidebar";
import { useNonDesktopViewport } from "../../../utils/hooks/useNonDesktopViewport";
import Icon from "../../../components/Icon/Icon";
import {
  AccessLevelE,
  CampaignWizardMode,
  IconColorE,
  IconSizeE,
  UserRoleE,
} from "../../../models/enums";
import AmountToggle from "../../../components/AmountToggle/AmountToggle";
import CountUp from "react-countup";
import { useCampaignWizard } from "../../../popups/CampaignWizard/useCampaignWizard";
import { useLazyGetNumberOfUnprocessedMessagesQuery } from "../../../api/supportMessagesAPI";
import { DEFAULT_PAGE_ITEMS_COUNT } from "../../../utils/constants";
import { useAppSelector } from "../../../store/hooks";
import {
  selectCurrentUser,
  selectIsChangingAccount,
} from "../../../store/selectors/authSelectors";
import AccessRights from "../../../components/AccessRights/AccessRights";
import Button, { ButtonIntentE } from "../../../components/Button/Button";
import { RoutesE } from "../../../routes/routesData";

import styles from "./../projects.module.scss";

/**
 * Main component for '/projects' route's page
 * */

type ProjectListPropTypes = {
  campaignNumber?: number | string;
};

const ProjectList: FC<ProjectListPropTypes> = memo(({ campaignNumber }) => {
  const navigate = useNavigate();
  const isNonDesktop = useNonDesktopViewport();
  const user: any = useAppSelector(selectCurrentUser);
  const isChangingAccount = useAppSelector(selectIsChangingAccount);
  const [searchParams] = useSearchParams();
  const [limit, setLimit] = useState(DEFAULT_PAGE_ITEMS_COUNT);
  const { take, page, pages, next, prev, setPage, setTotalCount, setTake } =
    usePagination();
  const { data, isLoading, isError, isFetching, refetch } = useGetProjectsQuery(
    {
      take,
      page,
      limit: take,
      statusName: searchParams.get("status"),
      sort: searchParams.get("sorting"),
      filterType: campaignNumber
        ? "campaignNumber"
        : searchParams.get("filterType"),
      filter: campaignNumber || searchParams.get("filter"),
    },
    {
      // Skip during an account switch so the query has no live subscription while the API state is
      // reset (mirrors CampaignList) — prevents a duplicate request and the deferred refetch() below
      // from crashing on a not-yet-started query.
      skip: isChangingAccount,
    }
  );
  const [getUnprocessedMessagesCount] =
    useLazyGetNumberOfUnprocessedMessagesQuery({});
  const [isSidebarHidden, setIsSidebarHidden] = useState(isNonDesktop);
  // Tracks which user the effect below last ran for, so the refetch fires on an ACCOUNT CHANGE only
  // and not on first mount. See the comment in that effect. Spec 34 D3.
  const lastUserNameRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    setIsSidebarHidden(isNonDesktop);
  }, [isNonDesktop]);
  const { createCampaignWizard } = useCampaignWizard();

  useEffect(() => {
    setTotalCount(data?.ItemCount || 0);
  }, [data]);

  // Return to the top of the scroll area on each new fetch (the pager sits at the end now).
  useEffect(() => {
    if (isFetching) {
      document.getElementById("app-scroll")?.scrollTo({ top: 0 });
    }
  }, [isFetching]);

  useEffect(() => {
    if (user.accessLevel === AccessLevelE.GlobalAdmin) {
      getUnprocessedMessagesCount({});
    }
    if (
      user.accessLevel !== AccessLevelE.DeliveryServicePrivate &&
      user.role === UserRoleE.DeliveryService
    ) {
      navigate(RoutesE.HOME_DELIVERY);
    }
    // Refetch ONLY when the account actually changed — never on first mount.
    //
    // useGetProjectsQuery has already fired by the time this effect runs, so an unconditional
    // refetch() made every /deliveries mount fetch the list TWICE (spec 34 D3). The refetch still
    // matters on an account switch, where we want the new user's data immediately — hence the ref
    // rather than deleting the call. CampaignList has no equivalent and is the reference for correct
    // first-mount behaviour.
    //
    // During an account switch the API state is reset (resetApiState) and this query is skipped
    // (isChangingAccount), so it has no active subscription. Calling refetch() in that window throws
    // "Cannot refetch a query that has not been started yet". Skip it — the query re-fetches for the
    // new user on its own once the switch completes. See KnownIssues "Account switch → refetch() on an
    // unstarted query".
    const previousUserName = lastUserNameRef.current;
    lastUserNameRef.current = user.userName;

    const isAccountChange =
      previousUserName !== null && previousUserName !== user.userName;

    if (isAccountChange && !isChangingAccount) {
      setTimeout(() => {
        refetch();
      }, 0);
    }
  }, [user.userName]);

  // handle create new campaign button click
  const navigateToCreateNewProject = () => {
    createCampaignWizard(null, CampaignWizardMode.CREATE);
  };

  const toggleSidebarVisibility = () => {
    setIsSidebarHidden((prev) => !prev);
  };

  const navigateToCreateCampaignPopup = () => {
    navigate(`${RoutesE.CAMPAIGNS}?wizard=true`);
  };

  return (
    <>
      <div
        className={styles.sidebarWrapper}
        style={{ left: isSidebarHidden ? "-100vw" : 0 }}
      >
        <Icon
          name="close"
          onClick={toggleSidebarVisibility}
          className={styles.closeSidebarButton}
          color={IconColorE.GRAY}
        />
        <StatusFiltersSidebar
          onAfterStatusSelect={() => setIsSidebarHidden(true)}
        />
      </div>
      <main className={styles.mainWrapper}>
        <section className={styles.headerSection}>
          <div className={styles.headerTopSection} id="headerTopSection">
            <div className={styles.titleSection} id="titleSection">
              <div className={styles.filterIconWrapper}>
                <Icon
                  name="filter"
                  color={IconColorE.WHITE}
                  onClick={toggleSidebarVisibility}
                />
              </div>
              <H1>
                {campaignNumber ? `Campaign ${campaignNumber}` : "Deliveries"}
              </H1>
              {isFetching ? (
                <Icon
                  name="loading"
                  size={IconSizeE.S}
                  color={IconColorE.LIGHT_GRAY}
                />
              ) : (
                <span className={styles.projectCount}>
                  (
                  <CountUp
                    end={data?.ItemCount || 0}
                    duration={0.3}
                    separator=","
                  />
                  {campaignNumber
                    ? ` project${data?.ItemCount > 1 ? "s" : ""}`
                    : ""}
                  )
                </span>
              )}
              {!isNonDesktop && <StatusTag />}
            </div>
            <AccessRights
              accessFor={[UserRoleE.AdvertisingAgency, UserRoleE.Broadcaster]}
              condition={user.accessLevel !== AccessLevelE.GlobalAdmin}
            >
              <Button
                icon="add"
                intent={ButtonIntentE.CAMPAIGN}
                onClick={
                  isNonDesktop
                    ? navigateToCreateNewProject
                    : navigateToCreateCampaignPopup
                }
                className={styles.addCampaignButton}
              >
                New campaign
              </Button>
            </AccessRights>
            <div id="search-delivery">
              <Search
                formID="search-projects"
                refetch={refetch}
                withAutocomplete
              />
            </div>
            {!!campaignNumber && (
              <div className={styles.orderWrapper}>
                <OrderBy />
              </div>
            )}
          </div>
        </section>
        <div
          className={isLoading ? styles.loadingWrapper : ""}
          id="delivery-list-wrapper"
        >
          <ProjectItemList
            items={data?.Projects}
            isFetching={isFetching}
            refetch={refetch}
          />
        </div>
        <div className={styles.pageFooter}>
          <AmountToggle changeAmount={setTake} labelText="" value={take} />
          <Pagination
            prev={prev}
            next={next}
            pages={pages}
            page={page}
            setPage={setPage}
          />
          <div className={styles.orderWrapper}>
            <OrderBy />
          </div>
        </div>
      </main>
    </>
  );
});

export default ProjectList;
